import { useState } from 'react';
import { auth, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

/**
 * "Wrong address? Fix it" — the escape hatch that makes verify-at-upgrade safe.
 *
 * Because nothing blocks a new account at signup, a mistyped address isn't
 * caught by a wall — so it has to be correctable at the two places we show the
 * address back (the post-signup notice and the upgrade gate). Without this a
 * typo is terminal: you can't confirm an inbox you don't own, and the password
 * reset goes to that same wrong inbox.
 *
 * The backend only allows this while the address is still unconfirmed, which is
 * what makes a session alone enough authorisation — an unverified account has
 * no billing attached and no proven identity, so there's nothing to steal.
 */
export function ChangeEmail({ onChanged }: { onChanged?: (email: string) => void }) {
  const { refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setErr(null);
    setBusy(true);
    try {
      const res = await auth.changeEmail(value.trim());
      // Pull the new address into the auth context so every other surface (the
      // rail, the upgrade gate) stops showing the old one.
      await refresh();
      setOpen(false);
      setValue('');
      onChanged?.(res.email);
    } catch (e2) {
      setErr((e2 as ApiError)?.detail || 'Could not update the address. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          font: 'inherit',
          fontSize: 12.5,
          color: 'var(--accent-purple)',
          cursor: 'pointer',
          textDecoration: 'underline',
        }}
      >
        Wrong address? Fix it
      </button>
    );
  }

  return (
    <form onSubmit={submit} style={{ display: 'grid', gap: 8 }}>
      <input
        className="rl-input"
        type="email"
        autoComplete="email"
        placeholder="Correct email address"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        required
        disabled={busy}
        autoFocus
      />
      {err && (
        <div role="alert" style={{ fontSize: 12.5, color: 'var(--mute-red)', lineHeight: 1.5 }}>
          {err}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="submit"
          className="rl-btn-primary"
          disabled={busy}
          style={{ padding: '8px 14px', fontSize: 13 }}
        >
          {busy ? 'Saving…' : 'Save & resend link'}
        </button>
        <button
          type="button"
          className="rl-btn-secondary"
          onClick={() => {
            setOpen(false);
            setErr(null);
          }}
          disabled={busy}
          style={{ padding: '8px 14px', fontSize: 13 }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
